import { DocumentType, Invoice, InvoiceStatus } from '../../types';
import { formatCents } from '../../services/money';
import type { AdvanceAmountChoice } from '../../services/invoicePickerFlow';

export type Filter = InvoiceStatus | 'all' | 'open';

/**
 * Die Statusfrage als Pillenreihe, wie bei den Angeboten.
 *
 * Bewusst nicht jeder Status: die Reihe soll man lesen koennen. Was fehlt,
 * findet man ueber die uebrigen Filter — was hier steht, ist die Frage, die
 * taeglich gestellt wird.
 */
export const STATUS_FILTERS: Array<{ key: Filter; label: string }> = [
    { key: 'all', label: 'Alle' },
    { key: 'open', label: 'Offen' },
    { key: 'OVERDUE', label: 'Überfällig' },
    { key: 'DRAFT', label: 'Entwürfe' },
    { key: 'READY_FOR_REVIEW', label: 'In Prüfung' },
    { key: 'ISSUED', label: 'Ausgestellt' },
    { key: 'PAID', label: 'Bezahlt' },
    { key: 'CANCELLED', label: 'Storniert' },
];

/** Belegarten fuer den Filter — kurz, in der Reihenfolge des Ablaufs. */
export const DOCUMENT_TYPE_FILTERS: Array<[DocumentType, string]> = [
    ['INVOICE', 'Rechnung'],
    ['ADVANCE_INVOICE', 'Abschlagsrechnung'],
    ['FINAL_INVOICE', 'Schlussrechnung'],
    ['REMAINING_INVOICE', 'Restrechnung'],
    ['CANCELLATION_INVOICE', 'Storno'],
    ['CORRECTION_INVOICE', 'Korrektur'],
    ['COMMERCIAL_CREDIT_INVOICE', 'Gutschrift'],
];

/** Kurzform fuer die Belegart in der Liste. */
export const DOCUMENT_TYPE_SHORT: Partial<Record<DocumentType, string>> = {
    ADVANCE_INVOICE: 'Abschlag',
    FINAL_INVOICE: 'Schluss',
    REMAINING_INVOICE: 'Rest',
    CANCELLATION_INVOICE: 'Storno',
    CORRECTION_INVOICE: 'Korrektur',
    COMMERCIAL_CREDIT_INVOICE: 'Gutschrift',
    SELF_BILLING_CREDIT_NOTE: 'Gutschrift (Kunde)',
};

export type SortKey = 'number' | 'customer' | 'project' | 'date' | 'due' | 'gross' | 'open';

/**
 * Belegarten, die aus einem Projekt heraus entstehen koennen.
 *
 * Storno, Korrektur und Gutschrift stehen bewusst NICHT hier: sie brauchen eine
 * Ursprungsrechnung und entstehen deshalb am Beleg, nicht am Projekt.
 */
export type CreatableDocumentType =
    | 'INVOICE'
    | 'ADVANCE_INVOICE'
    | 'FINAL_INVOICE'
    | 'REMAINING_INVOICE';

/**
 * Die zuletzt gewaehlte Belegart. Eine Bequemlichkeit des Arbeitsplatzes,
 * bewusst im Browser und nicht am Benutzerprofil.
 *
 * NUR die Belegart, und das ist der Kern. Hier standen frueher auch
 * `cumulative` und `deductAdvances`. Beide sind Eigenschaften des BELEGS und
 * haengen am Projekt — die eine an dessen Abrechnungshistorie, die andere an
 * dessen Abschlaegen. Projektuebergreifend gemerkt vererbten sie sich von
 * Projekt zu Projekt: genau so entstand Vorfall 6172 (billingMode.ts:4), und
 * ein aus der letzten Sitzung mitgebrachter Abschlagsabzug ist derselbe
 * Fehlertyp. Beide werden jetzt nach dem Klick auf ein Projekt gefragt, und
 * nur dort, wo sie fuer dieses Projekt ueberhaupt offen sind.
 *
 * Die Belegart darf bleiben: sie steht sichtbar in der Kopfleiste, sie
 * entscheidet, welche Projekte die Liste zeigt, und eine falsche Wahl faellt
 * sofort auf.
 */
const DOC_PREFERENCE_KEY = 'invoices.newDocumentPreference';

interface DocPreference {
    docType: CreatableDocumentType;
}

export const readDocPreference = (): DocPreference | null => {
    try {
        const raw = localStorage.getItem(DOC_PREFERENCE_KEY);
        if (!raw) return null;
        const parsed = JSON.parse(raw) as Partial<DocPreference>;
        // Gegen die bekannten Arten pruefen: ein Eintrag aus einer aelteren
        // Fassung darf keine Belegart erzeugen, die es nicht mehr gibt.
        if (!DOC_TYPE_CHOICES.some((c) => c.type === parsed.docType)) return null;
        // `cumulative` und `deductAdvances` aus einer aelteren Fassung werden
        // stillschweigend fallen gelassen — sie haben keine Bedeutung mehr.
        return { docType: parsed.docType as CreatableDocumentType };
    } catch {
        // Kein Speicher (privater Modus, gesperrte Seite) oder kaputter
        // Eintrag: dann eben ohne Gedaechtnis, aber nicht ohne Ansicht.
        return null;
    }
};

export const writeDocPreference = (pref: DocPreference): void => {
    try {
        localStorage.setItem(DOC_PREFERENCE_KEY, JSON.stringify(pref));
    } catch {
        /* Ohne Speicher gilt die Wahl nur fuer diese Sitzung. */
    }
};

const MONTHS_SHORT = [
    'Jan.',
    'Feb.',
    'März',
    'Apr.',
    'Mai',
    'Juni',
    'Juli',
    'Aug.',
    'Sep.',
    'Okt.',
    'Nov.',
    'Dez.',
];

/** 'JJJJ-MM' -> 'Mai 26'. Kurz genug, damit mehrere Monate in eine Zeile passen. */
export const monthLabel = (month: string | null): string => {
    if (!month) return 'ohne Datum';
    const [year, m] = month.split('-');
    return `${MONTHS_SHORT[Number(m) - 1] ?? m} ${year.slice(2)}`;
};

/**
 * 'JJJJ-MM-TT' -> 'TT.MM.JJJJ'.
 *
 * Rein ueber die Zeichenkette und bewusst ohne Date: ein aus dem ISO-Datum
 * gebautes Date liegt in UTC, und die oertliche Ausgabe schoebe den Beleg
 * westlich von Greenwich auf den Vortag.
 */
export const dayLabel = (iso: string): string => iso.split('-').reverse().join('.');

/**
 * Der gewaehlte Zeitraum in einem Satz.
 *
 * Fuer die Zusammenfassung im Anlagedialog: die drei Felder (Monat, von, bis)
 * lassen sich einzeln verstellen, und ob am Ende ein sinnvolles Fenster steht,
 * sah man ihnen nicht an — offen ist genauso ein Ergebnis wie ein Monat.
 */
export const describePeriod = (from: string, to: string): string => {
    if (!from && !to) return 'Ohne Zeitraum — alles, was offen ist.';
    if (from && to) return `Leistungszeitraum ${dayLabel(from)} – ${dayLabel(to)}`;
    if (from) return `Leistungszeitraum ab ${dayLabel(from)}, ohne Ende`;
    return `Leistungszeitraum bis ${dayLabel(to)}, ohne Beginn`;
};

/** Belege oder Buchhaltung — zwei Sichten auf denselben Vorgang. */
export type Section = 'documents' | 'accounting';

export const DOC_TYPE_CHOICES: Array<{
    type: CreatableDocumentType;
    label: string;
    hint: string;
}> = [
    {
        type: 'INVOICE',
        label: 'Rechnung',
        hint: 'Die offenen Positionen des Leistungsverzeichnisses. Mit Leistungszeitraum nur die dort protokollierten Mengen — die Teilrechnung über einen Monat. Die abgerechneten Mengen werden ins Projekt zurückgeschrieben und tauchen im nächsten Monat nicht wieder auf.',
    },
    {
        type: 'ADVANCE_INVOICE',
        label: 'Abschlagsrechnung',
        hint: 'Eine Pauschale auf den Gesamtauftrag, nach Prozentsatz oder Festbetrag. Schreibt keine Mengen ins Projekt zurück — die Leistung ist noch nicht erbracht — und wird später auf der Schlussrechnung abgezogen.',
    },
    {
        type: 'FINAL_INVOICE',
        label: 'Schlussrechnung',
        hint: 'Alle noch offenen Positionen. Bereits gestellte Abschläge werden je Steuersatz abgezogen; ohne Abschläge entsteht kein Abzugsblock. Für das abgeschlossene Projekt.',
    },
    {
        type: 'REMAINING_INVOICE',
        label: 'Restrechnung',
        hint: 'Wie die Schlussrechnung, aber das Projekt läuft weiter.',
    },
];

export const OPEN_STATUSES: InvoiceStatus[] = ['ISSUED', 'SENT', 'PARTIALLY_PAID', 'OVERDUE'];

export const de = (iso?: string): string => (iso ? iso.split('-').reverse().join('.') : '—');

/**
 * Hinweis am offenen Posten, wenn ein Sicherheitseinbehalt im Spiel ist.
 *
 * Der Einbehalt steckt im Zahlbetrag und damit im offenen Posten, erscheint
 * aber in keiner Spalte der Liste. Ohne diesen Hinweis stuende dort eine Zahl,
 * die kleiner ist als das Brutto daneben, ohne erkennbaren Grund — und beim
 * Bankabgleich wuerde der fehlende Betrag als Unterzahlung gelesen.
 *
 * Kein eigener Spaltenkopf: die Liste ist bereits acht Spalten breit, und die
 * Angabe betrifft nur die wenigen Belege mit Einbehalt.
 */
export const retentionHint = (inv: Invoice): string | undefined => {
    // Kumulatives Modell (Bauplan §16): der Beleg nennt den Einbehalt auf den
    // Leistungsstand, davon den Teil dieses Belegs, bzw. die Freigabe.
    const cumulative = inv.totals.cumulativeRetentionCents ?? 0;
    const held = inv.totals.retentionCents ?? 0;
    const released = inv.totals.retentionReleaseCents ?? 0;
    if (cumulative === 0 && held === 0 && released === 0) return undefined;
    const parts: string[] = [];
    if (cumulative !== 0) parts.push(`Sicherheitseinbehalt kumuliert ${formatCents(cumulative)}`);
    if (held !== 0) parts.push(`davon mit diesem Beleg einbehalten ${formatCents(held)}`);
    if (released !== 0) parts.push(`Sicherheitseinbehalt freigegeben ${formatCents(released)}`);
    return `Zahlbetrag ${formatCents(inv.totals.payableCents)} — ${parts.join(', ')}`;
};

/** Der leere Entwurf der Abschlagshoehe — die Vorbelegung jedes neuen Projekts. */
export const EMPTY_ADVANCE_DRAFT: AdvanceAmountChoice = { kind: 'percent', percent: '30', fixed: '' };
